import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { api_url } from "../../utils/utils";

/* =========================================================
   COMMON CONFIG
========================================================= */

const getConfig = (getState) => {
  const token = getState().auth.token;

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const getError = (error, defaultMessage = "Something went wrong") => {
  return (
    error.response?.data || {
      message: error.message || defaultMessage,
    }
  );
};

/* =========================================================
   CREATE QUIZ
========================================================= */

export const add_quiz = createAsyncThunk(
  "quiz/add_quiz",
  async (info, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.post(
        `${api_url}/api/quiz/create`,
        info,
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("add_quiz error:", error);

      return rejectWithValue(
        getError(error, "Quiz তৈরি করা যায়নি")
      );
    }
  }
);

/* =========================================================
   GET ALL QUIZZES
========================================================= */

export const get_quizzes = createAsyncThunk(
  "quiz/get_quizzes",
  async (params = {}, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = {
      ...getConfig(getState),
      params,
    };

    try {
      const { data } = await axios.get(
        `${api_url}/api/quiz/get-all`,
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("get_quizzes error:", error);

      return rejectWithValue(
        getError(error, "Quiz list পাওয়া যায়নি")
      );
    }
  }
);

/* =========================================================
   GET SINGLE QUIZ
========================================================= */

export const get_quiz = createAsyncThunk(
  "quiz/get_quiz",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.get(
        `${api_url}/api/quiz/get/${id}`,
        config
      );
      console.log(data)
      return fulfillWithValue(data);
    } catch (error) {
      console.log("get_quiz error:", error);

      return rejectWithValue(
        getError(error, "Quiz পাওয়া যায়নি")
      );
    }
  }
);

/* =========================================================
   UPDATE QUIZ
========================================================= */

export const update_quiz = createAsyncThunk(
  "quiz/update_quiz",
  async (
    { id, info },
    { fulfillWithValue, rejectWithValue, getState }
  ) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.put(
        `${api_url}/api/quiz/update/${id}`,
        info,
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("update_quiz error:", error);

      return rejectWithValue(
        getError(error, "Quiz update করা যায়নি")
      );
    }
  }
);

/* =========================================================
   DELETE QUIZ
========================================================= */

export const delete_quiz = createAsyncThunk(
  "quiz/delete_quiz",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.delete(
        `${api_url}/api/quiz/delete/${id}`,
        config
      );

      return fulfillWithValue({
        ...data,
        deletedId: id,
      });
    } catch (error) {
      console.log("delete_quiz error:", error);

      return rejectWithValue(
        getError(error, "Quiz delete করা যায়নি")
      );
    }
  }
);

/* =========================================================
   PUBLISH QUIZ
========================================================= */

export const publish_quiz = createAsyncThunk(
  "quiz/publish_quiz",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.put(
        `${api_url}/api/quiz/publish/${id}`,
        {},
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("publish_quiz error:", error);

      return rejectWithValue(
        getError(error, "Quiz publish করা যায়নি")
      );
    }
  }
);

/* =========================================================
   UNPUBLISH QUIZ
========================================================= */

export const unpublish_quiz = createAsyncThunk(
  "quiz/unpublish_quiz",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.put(
        `${api_url}/api/quiz/unpublish/${id}`,
        {},
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("unpublish_quiz error:", error);

      return rejectWithValue(
        getError(error, "Quiz unpublish করা যায়নি")
      );
    }
  }
);

/* =========================================================
   GET QUIZ PARTICIPANTS
========================================================= */

export const get_quiz_participants = createAsyncThunk(
  "quiz/get_quiz_participants",
  async (
    { id, params = {} },
    { fulfillWithValue, rejectWithValue, getState }
  ) => {
    const config = {
      ...getConfig(getState),
      params,
    };

    try {
      const { data } = await axios.get(
        `${api_url}/api/quiz/participants/${id}`,
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("get_quiz_participants error:", error);

      return rejectWithValue(
        getError(error, "Participants পাওয়া যায়নি")
      );
    }
  }
);

/* =========================================================
   GET SINGLE PARTICIPANT
========================================================= */

export const get_quiz_participant = createAsyncThunk(
  "quiz/get_quiz_participant",
  async (
    { quizId, participantId },
    { fulfillWithValue, rejectWithValue, getState }
  ) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.get(
        `${api_url}/api/quiz/${quizId}/participant/${participantId}`,
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("get_quiz_participant error:", error);

      return rejectWithValue(
        getError(error, "Participant details পাওয়া যায়নি")
      );
    }
  }
);

/* =========================================================
   GET QUIZ STATISTICS
========================================================= */

export const get_quiz_statistics = createAsyncThunk(
  "quiz/get_quiz_statistics",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.get(
        `${api_url}/api/quiz/statistics/${id}`,
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("get_quiz_statistics error:", error);

      return rejectWithValue(
        getError(error, "Quiz statistics পাওয়া যায়নি")
      );
    }
  }
);

/* =========================================================
   GET WINNERS
========================================================= */

export const get_quiz_winners = createAsyncThunk(
  "quiz/get_quiz_winners",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.get(
        `${api_url}/api/quiz/winners/${id}`,
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("get_quiz_winners error:", error);

      return rejectWithValue(
        getError(error, "Winner list পাওয়া যায়নি")
      );
    }
  }
);

/* =========================================================
   SELECT WINNERS
========================================================= */

export const select_quiz_winners = createAsyncThunk(
  "quiz/select_quiz_winners",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.post(
        `${api_url}/api/quiz/select-winners/${id}`,
        {},
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("select_quiz_winners error:", error);

      return rejectWithValue(
        getError(error, "Winner select করা যায়নি")
      );
    }
  }
);

/* =========================================================
   PUBLISH WINNERS
========================================================= */

export const publish_quiz_winners = createAsyncThunk(
  "quiz/publish_quiz_winners",
  async (id, { fulfillWithValue, rejectWithValue, getState }) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.put(
        `${api_url}/api/quiz/publish-winners/${id}`,
        {},
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("publish_quiz_winners error:", error);

      return rejectWithValue(
        getError(error, "Winner publish করা যায়নি")
      );
    }
  }
);

/* =========================================================
   UPDATE WINNER CLAIM STATUS
========================================================= */

export const update_winner_claim = createAsyncThunk(
  "quiz/update_winner_claim",
  async (
    { quizId, winnerId, status },
    { fulfillWithValue, rejectWithValue, getState }
  ) => {
    const config = getConfig(getState);

    try {
      const { data } = await axios.put(
        `${api_url}/api/quiz/winner-claim/${quizId}/${winnerId}`,
        { status },
        config
      );

      return fulfillWithValue(data);
    } catch (error) {
      console.log("update_winner_claim error:", error);

      return rejectWithValue(
        getError(error, "Winner claim status update করা যায়নি")
      );
    }
  }
);

/* =========================================================
   INITIAL STATE
========================================================= */

export const quizReducer = createSlice({
  name: "quiz",

  initialState: {
    successMessage: "",
    errorMessage: "",

    loader: false,

    quizzes: [],
    quiz: null,

    participants: [],
    participant: null,

    winners: [],
    statistics: null,

    totalQuiz: 0,
    totalParticipants: 0,
    totalWinners: 0,

    pagination: {
      currentPage: 1,
      totalPages: 1,
      limit: 10,
    },
  },

  reducers: {
    messageClear: (state, _) => {
      state.errorMessage = "";
      state.successMessage = "";
    },

    quizClear: (state, _) => {
      state.quiz = null;
    },

    participantClear: (state, _) => {
      state.participant = null;
    },

    statisticsClear: (state, _) => {
      state.statistics = null;
    },

    winnersClear: (state, _) => {
      state.winners = [];
    },
  },

  /* =========================================================
     EXTRA REDUCERS
  ========================================================= */

  extraReducers: {
    /* =====================================================
       ADD QUIZ
    ===================================================== */

    [add_quiz.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [add_quiz.rejected]: (state, { payload }) => {
      state.loader = false;
      state.errorMessage =
        payload?.message || "Quiz তৈরি করা যায়নি";
    },

    [add_quiz.fulfilled]: (state, { payload }) => {
      state.loader = false;

      state.successMessage =
        payload?.message || "Quiz সফলভাবে তৈরি হয়েছে";

      if (payload?.quiz) {
        state.quiz = payload.quiz;

        state.quizzes = [
          payload.quiz,
          ...state.quizzes,
        ];
      }
    },

    /* =====================================================
       GET ALL QUIZZES
    ===================================================== */

    [get_quizzes.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [get_quizzes.rejected]: (state, { payload }) => {
      state.loader = false;

      state.errorMessage =
        payload?.message || "Quiz list পাওয়া যায়নি";
    },

    [get_quizzes.fulfilled]: (state, { payload }) => {
      state.loader = false;

      state.quizzes =
        payload?.quizzes ||
        payload?.data ||
        [];

      state.totalQuiz =
        payload?.totalQuiz ||
        payload?.total ||
        state.quizzes.length;

      if (payload?.pagination) {
        state.pagination =
          payload.pagination;
      }
    },

    /* =====================================================
       GET SINGLE QUIZ
    ===================================================== */

    [get_quiz.pending]: (state, _) => {
      state.loader = true;
      state.quiz = null;
      state.errorMessage = "";
    },

    [get_quiz.rejected]: (state, { payload }) => {
      state.loader = false;
      state.quiz = null;

      state.errorMessage =
        payload?.message || "Quiz পাওয়া যায়নি";
    },

    [get_quiz.fulfilled]: (state, { payload }) => {
      state.loader = false;

      state.quiz =
        payload?.quiz ||
        payload?.data ||
        payload;
    },

    /* =====================================================
       UPDATE QUIZ
    ===================================================== */

    [update_quiz.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [update_quiz.rejected]: (state, { payload }) => {
      state.loader = false;

      state.errorMessage =
        payload?.message || "Quiz update করা যায়নি";
    },

    [update_quiz.fulfilled]: (state, { payload }) => {
      state.loader = false;

      state.successMessage =
        payload?.message || "Quiz update হয়েছে";

      if (payload?.quiz) {
        state.quiz = payload.quiz;

        const index =
          state.quizzes.findIndex(
            (item) =>
              item._id === payload.quiz._id
          );

        if (index !== -1) {
          state.quizzes[index] =
            payload.quiz;
        }
      }
    },

    /* =====================================================
       DELETE QUIZ
    ===================================================== */

    [delete_quiz.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [delete_quiz.rejected]: (state, { payload }) => {
      state.loader = false;

      state.errorMessage =
        payload?.message || "Quiz delete করা যায়নি";
    },

    [delete_quiz.fulfilled]: (state, { payload }) => {
      state.loader = false;

      state.successMessage =
        payload?.message || "Quiz delete হয়েছে";

      state.quizzes =
        state.quizzes.filter(
          (item) =>
            item._id !== payload?.deletedId
        );

      state.totalQuiz =
        Math.max(0, state.totalQuiz - 1);

      if (
        state.quiz?._id ===
        payload?.deletedId
      ) {
        state.quiz = null;
      }
    },

    /* =====================================================
       PUBLISH QUIZ
    ===================================================== */

    [publish_quiz.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [publish_quiz.rejected]: (state, { payload }) => {
      state.loader = false;

      state.errorMessage =
        payload?.message || "Quiz publish করা যায়নি";
    },

    [publish_quiz.fulfilled]: (state, { payload }) => {
      state.loader = false;

      state.successMessage =
        payload?.message || "Quiz publish হয়েছে";

      if (payload?.quiz) {
        state.quiz = payload.quiz;

        const index =
          state.quizzes.findIndex(
            (item) =>
              item._id === payload.quiz._id
          );

        if (index !== -1) {
          state.quizzes[index] =
            payload.quiz;
        }
      }
    },

    /* =====================================================
       UNPUBLISH QUIZ
    ===================================================== */

    [unpublish_quiz.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [unpublish_quiz.rejected]: (state, { payload }) => {
      state.loader = false;

      state.errorMessage =
        payload?.message ||
        "Quiz unpublish করা যায়নি";
    },

    [unpublish_quiz.fulfilled]: (state, { payload }) => {
      state.loader = false;

      state.successMessage =
        payload?.message ||
        "Quiz unpublish হয়েছে";

      if (payload?.quiz) {
        state.quiz = payload.quiz;

        const index =
          state.quizzes.findIndex(
            (item) =>
              item._id === payload.quiz._id
          );

        if (index !== -1) {
          state.quizzes[index] =
            payload.quiz;
        }
      }
    },

    /* =====================================================
       PARTICIPANTS
    ===================================================== */

    [get_quiz_participants.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [get_quiz_participants.rejected]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.errorMessage =
        payload?.message ||
        "Participants পাওয়া যায়নি";
    },

    [get_quiz_participants.fulfilled]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.participants =
        payload?.participants ||
        payload?.data ||
        [];

      state.totalParticipants =
        payload?.totalParticipants ||
        payload?.total ||
        state.participants.length;

      if (payload?.pagination) {
        state.pagination =
          payload.pagination;
      }
    },

    /* =====================================================
       SINGLE PARTICIPANT
    ===================================================== */

    [get_quiz_participant.pending]: (state, _) => {
      state.loader = true;
      state.participant = null;
      state.errorMessage = "";
    },

    [get_quiz_participant.rejected]: (
      state,
      { payload }
    ) => {
      state.loader = false;
      state.participant = null;

      state.errorMessage =
        payload?.message ||
        "Participant পাওয়া যায়নি";
    },

    [get_quiz_participant.fulfilled]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.participant =
        payload?.participant ||
        payload?.data ||
        payload;
    },

    /* =====================================================
       STATISTICS
    ===================================================== */

    [get_quiz_statistics.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [get_quiz_statistics.rejected]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.errorMessage =
        payload?.message ||
        "Statistics পাওয়া যায়নি";
    },

    [get_quiz_statistics.fulfilled]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.statistics =
        payload?.statistics ||
        payload?.data ||
        payload;
    },

    /* =====================================================
       GET WINNERS
    ===================================================== */

    [get_quiz_winners.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [get_quiz_winners.rejected]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.errorMessage =
        payload?.message ||
        "Winner list পাওয়া যায়নি";
    },

    [get_quiz_winners.fulfilled]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.winners =
        payload?.winners ||
        payload?.data ||
        [];

      state.totalWinners =
        payload?.totalWinners ||
        state.winners.length;
    },

    /* =====================================================
       SELECT WINNERS
    ===================================================== */

    [select_quiz_winners.pending]: (state, _) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [select_quiz_winners.rejected]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.errorMessage =
        payload?.message ||
        "Winner select করা যায়নি";
    },

    [select_quiz_winners.fulfilled]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.successMessage =
        payload?.message ||
        "Winner successfully selected";

      state.winners =
        payload?.winners ||
        state.winners;
    },

    /* =====================================================
       PUBLISH WINNERS
    ===================================================== */

    [publish_quiz_winners.pending]: (
      state,
      _
    ) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [publish_quiz_winners.rejected]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.errorMessage =
        payload?.message ||
        "Winner publish করা যায়নি";
    },

    [publish_quiz_winners.fulfilled]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.successMessage =
        payload?.message ||
        "Winner successfully published";

      state.winners =
        payload?.winners ||
        state.winners;
    },

    /* =====================================================
       UPDATE WINNER CLAIM
    ===================================================== */

    [update_winner_claim.pending]: (
      state,
      _
    ) => {
      state.loader = true;
      state.errorMessage = "";
    },

    [update_winner_claim.rejected]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.errorMessage =
        payload?.message ||
        "Claim status update করা যায়নি";
    },

    [update_winner_claim.fulfilled]: (
      state,
      { payload }
    ) => {
      state.loader = false;

      state.successMessage =
        payload?.message ||
        "Claim status update হয়েছে";

      if (payload?.winner) {
        const index =
          state.winners.findIndex(
            (item) =>
              item._id ===
              payload.winner._id
          );

        if (index !== -1) {
          state.winners[index] =
            payload.winner;
        }
      }
    },
  },
});

/* =========================================================
   ACTIONS
========================================================= */

export const {
  messageClear,
  quizClear,
  participantClear,
  statisticsClear,
  winnersClear,
} = quizReducer.actions;

/* =========================================================
   REDUCER
========================================================= */

export default quizReducer.reducer;